import { Controller } from "@hotwired/stimulus"
import { confirmDialog } from "../app/turbo_confirm"

// Handles the preview-then-confirm flow for the DB Maintenance admin page.
//
// Usage:
//   <form data-controller="db-maintenance-form"
//         data-db-maintenance-form-table-value="versions"
//         data-db-maintenance-form-preview-url-value="/admin/db_maintenance/preview"
//         data-db-maintenance-form-purge-url-value="/admin/db_maintenance/purge">
//     <!-- filter inputs marked with data-filter="field_name" -->
//     <button type="button" data-action="db-maintenance-form#preview">Preview</button>
//     <span data-db-maintenance-form-target="previewCount"></span>
//     <button type="button" data-db-maintenance-form-target="purgeButton" disabled
//             data-action="db-maintenance-form#purge">Purge</button>
//   </form>
export default class extends Controller {
  static targets = ["previewCount", "purgeButton"]
  static values  = { table: String, previewUrl: String, purgeUrl: String }

  async preview(event) {
    event.preventDefault()
    this._setPreviewText("Checking…", "text-muted")

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: this._formData()
      })

      if (!response.ok) {
        this._setPreviewText(`Preview failed (HTTP ${response.status})`, "text-danger")
        this.purgeButtonTarget.disabled = true
        return
      }

      const data = await response.json()
      const count = data.count | 0
      const noun = count === 1 ? "row" : "rows"
      this._setPreviewText(`${count.toLocaleString()} ${noun} from ${this.tableValue} would be deleted.`, "text-body")
      this.purgeButtonTarget.disabled = count === 0
      this.purgeButtonTarget.dataset.previewedCount = String(count)
    } catch (err) {
      this._setPreviewText(`Preview error: ${err.message}`, "text-danger")
      this.purgeButtonTarget.disabled = true
    }
  }

  async purge(event) {
    event.preventDefault()
    const count = parseInt(this.purgeButtonTarget.dataset.previewedCount || "0", 10)
    if (count === 0) return
    const msg = `Permanently delete ${count.toLocaleString()} rows from ${this.tableValue}? This cannot be undone.`
    if (!(await confirmDialog(msg))) return

    const form = document.createElement("form")
    form.method = "post"
    form.action = this.purgeUrlValue
    form.appendChild(this._hiddenField("authenticity_token", this._csrfToken()))
    form.appendChild(this._hiddenField("table", this.tableValue))
    this._filterFields().forEach(([k, v]) => form.appendChild(this._hiddenField(`filters[${k}]`, v)))
    document.body.appendChild(form)
    form.submit()
  }

  _setPreviewText(text, cssClass) {
    const el = this.previewCountTarget
    el.textContent = text
    el.classList.remove("text-muted", "text-danger", "text-body")
    el.classList.add(cssClass)
  }

  _formData() {
    const fd = new FormData()
    fd.append("table", this.tableValue)
    this._filterFields().forEach(([k, v]) => fd.append(`filters[${k}]`, v))
    return fd
  }

  _filterFields() {
    return Array.from(this.element.querySelectorAll("[data-filter]"))
      .filter((el) => (el.type === "checkbox" ? el.checked : el.value !== ""))
      .map((el)   => [el.dataset.filter, el.type === "checkbox" ? "1" : el.value])
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _hiddenField(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }
}
